import {
  parseSearchNumberValue,
  parseSearchStringValue,
  parseSearchArrayValue,
  parseSearchBooleanValue,
} from 'infrastructure/functions/parse-query-string';
import { useQueryFilterNew } from 'infrastructure/hooks/common/use-query-param-new';

import type { TParseFunctions } from 'infrastructure/hooks/common/use-query-param-new';
import type {
  IFollowUpTableDataFilters,
  INewlyTableDataFilters,
  IPartialsTableDataFilters,
  ISearchPatientFilters,
  PodFilterValue,
} from 'infrastructure/interfaces';

const VALID_POD_FILTER_VALUES: ReadonlySet<string> = new Set([
  'primary',
  'secondary',
  'tertiary',
  'outside',
]);

const parsePodFilterValue = (
  value: string | null,
): PodFilterValue | undefined => {
  if (value && VALID_POD_FILTER_VALUES.has(value))
    return value as PodFilterValue;
  return undefined;
};

export const defaultNewlyValues: INewlyTableDataFilters = {
  'n-page': 1,
  'n-excludeClinicGuid': [],
  'n-clinicGuid': [],
  'n-allExcluded': false,
};

const parseNewlyFunctions: TParseFunctions<INewlyTableDataFilters> = {
  'n-page': parseSearchNumberValue,
  'n-name': parseSearchStringValue,
  'n-dueDate': parseSearchStringValue,
  'n-provider': parseSearchStringValue,
  'n-enrolledDate': parseSearchStringValue,
  'n-lastCallDate': parseSearchStringValue,
  'n-appointmentDate': parseSearchStringValue,
  'n-type': parseSearchStringValue,
  'n-clinicGuid': parseSearchArrayValue,
  'n-excludeClinicGuid': parseSearchArrayValue,
  'n-allExcluded': parseSearchBooleanValue,
  'n-numberOfCalls': parseSearchNumberValue,
  'n-calls': parseSearchStringValue,
  podFilter: parsePodFilterValue,
  patientGuid: parseSearchStringValue,
  ccmCaseManagerGuid: parseSearchStringValue,
};

export const useNewlyTableDataQueryFilter = () => {
  return useQueryFilterNew<INewlyTableDataFilters>(
    defaultNewlyValues,
    parseNewlyFunctions,
  );
};

export const defaultFollowUpValues: IFollowUpTableDataFilters = {
  'f-page': 1,
};

const parseFollowUpFunctions: TParseFunctions<IFollowUpTableDataFilters> = {
  'f-page': parseSearchNumberValue,
  'f-name': parseSearchStringValue,
  'f-dueDate': parseSearchStringValue,
  'f-provider': parseSearchStringValue,
  'f-callsMonth': parseSearchStringValue,
  'f-lastBilledDate': parseSearchStringValue,
  'f-appointmentDate': parseSearchStringValue,
  'f-type': parseSearchStringValue,
  'f-clinicGuid': parseSearchArrayValue,
  'f-excludeClinicGuid': parseSearchArrayValue,
  'f-allExcluded': parseSearchBooleanValue,
  'f-numberOfCalls': parseSearchNumberValue,
  'f-actionTimes': parseSearchStringValue,
  'f-ccmInteractionStatus': parseSearchStringValue,
  'f-completed': parseSearchStringValue,
  podFilter: parsePodFilterValue,
  patientGuid: parseSearchStringValue,
  ccmCaseManagerGuid: parseSearchStringValue,
};

export const useFollowUpTableDataQueryFilter = () => {
  return useQueryFilterNew<IFollowUpTableDataFilters>(
    defaultFollowUpValues,
    parseFollowUpFunctions,
  );
};

export const defaultPartialsValues: IPartialsTableDataFilters = {
  'p-page': 1,
  ccmInteractionStatus: true,
};

const parsePartialsFunctions: TParseFunctions<IPartialsTableDataFilters> = {
  'p-page': parseSearchNumberValue,
  'p-name': parseSearchStringValue,
  'p-dueDate': parseSearchStringValue,
  'p-provider': parseSearchStringValue,
  'p-callsMonth': parseSearchStringValue,
  'p-nextBillable': parseSearchStringValue,
  'p-appointmentDate': parseSearchStringValue,
  'p-minutes': parseSearchStringValue,
  'p-lastCallDate': parseSearchStringValue,
  'p-type': parseSearchStringValue,
  'p-clinicGuid': parseSearchArrayValue,
  'p-excludeClinicGuid': parseSearchArrayValue,
  'p-allExcluded': parseSearchBooleanValue,
  podFilter: parsePodFilterValue,
  patientGuid: parseSearchStringValue,
  ccmInteractionStatus: parseSearchBooleanValue,
  ccmCaseManagerGuid: parseSearchStringValue,
};

export const usePartialsTableDataQueryFilter = () => {
  return useQueryFilterNew<IPartialsTableDataFilters>(
    defaultPartialsValues,
    parsePartialsFunctions,
  );
};

const parseSearchPatientFunctions: TParseFunctions<ISearchPatientFilters> = {
  'n-page': parseSearchNumberValue,
  'f-page': parseSearchNumberValue,
  'p-page': parseSearchNumberValue,
  patientGuid: parseSearchStringValue,
  ccmCaseManagerGuid: parseSearchStringValue,
};

export const useSearchPatientQueryFilter = () => {
  return useQueryFilterNew<ISearchPatientFilters>(
    {},
    parseSearchPatientFunctions,
  );
};

export const useTableFiltersMap = () => {
  const { filter: newlyTableFilter } = useNewlyTableDataQueryFilter();
  const { filter: followUpTableFilter } = useFollowUpTableDataQueryFilter();
  const { filter: partialsTableFilter } = usePartialsTableDataQueryFilter();

  return {
    newly: newlyTableFilter,
    'follow-up': followUpTableFilter,
    partials: partialsTableFilter,
  };
};
